import React from 'react';
import ReactDOM from 'react-dom';

const shell = {
  position: 'fixed',
  inset: 0,
  zIndex: 50000,
  background: 'rgba(248,249,250,0.97)',
  overflow: 'auto',
  padding: 16
};

const card = {
  maxWidth: 720,
  margin: '32px auto',
  padding: 28,
  background: '#fff',
  color: '#111',
  borderRadius: 8,
  boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
  fontFamily: 'system-ui, sans-serif'
};

const btn = {
  background: '#1976d2',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  padding: '10px 16px',
  fontSize: 15,
  cursor: 'pointer'
};

const btnPrimary = {
  ...btn,
  padding: '12px 22px',
  fontWeight: 600
};

function Row({ ok, pending, label }) {
  const mark = ok ? '✓' : pending ? '○' : '✗';
  return (
    <li style={{ margin: '6px 0', listStyle: 'none' }}>
      <span style={{ marginRight: 8 }}>{mark}</span>
      {label}
    </li>
  );
}

export default function SecureExamPreflight({
  capabilities,
  missing,
  policy,
  isFullscreen,
  screenShareActive,
  environmentCheckComplete = true,
  requireEnvironmentCheck = false,
  onEnterFullscreen,
  onStartScreenShare,
  shareError,
  onBeginCheck,
  canStartExam,
  onStartExam,
  phase
}) {
  const shareOk = !policy.requireScreenShare || screenShareActive;
  const showShareButton = policy.requireScreenShare && !screenShareActive;
  const showFullscreenButton = shareOk && !isFullscreen;

  let inner;
  if (missing && missing.length) {
    inner = (
      <div style={card}>
        <h2>Browser not supported</h2>
        <p>
          Your browser does not meet this examination&apos;s security requirements ({missing.join(', ')}).
          Use the institution&apos;s supported browser (current Chrome or Edge recommended).
        </p>
      </div>
    );
  } else if (canStartExam) {
    inner = (
      <div style={card}>
        <h2>Secure environment verified</h2>
        <p>Environment check, screen sharing, and fullscreen are complete.</p>
        <button type="button" style={btnPrimary} onClick={onStartExam}>
          Start Exam
        </button>
      </div>
    );
  } else {
    inner = (
      <div style={card}>
        <h2>Secure exam check</h2>
        <p style={{ lineHeight: 1.5 }}>
          Complete these steps in order: environment check, share your entire screen, then enter fullscreen.
        </p>
        <ul style={{ padding: 0 }}>
          <Row ok={capabilities.fullscreenSupported} label="Browser compatible" />
          <Row ok={capabilities.visibilityApiSupported} label="Network available" />
          {requireEnvironmentCheck && (
            <Row ok={environmentCheckComplete} pending={!environmentCheckComplete} label="Environment check" />
          )}
          {policy.requireScreenShare && (
            <Row ok={screenShareActive} pending={!screenShareActive} label="Screen sharing (entire screen)" />
          )}
          <Row ok={isFullscreen} pending={!isFullscreen} label="Fullscreen mode" />
        </ul>
        <h3 style={{ fontSize: 15, marginTop: 16 }}>Exam restrictions</h3>
        <ul style={{ padding: 0 }}>
          {policy.blockCopy && <Row ok label="Copy and paste disabled" />}
          {policy.blockPrint && <Row ok label="Printing disabled" />}
          {policy.blockExternalLinks && <Row ok label="External navigation blocked" />}
          {policy.detectTabSwitch && <Row ok label="Tab switching monitored" />}
          {policy.requireScreenShare && <Row ok label="Screen sharing required" />}
        </ul>
        {shareError ? (
          <p style={{ color: '#b71c1c', lineHeight: 1.5, marginTop: 16 }}>{shareError}</p>
        ) : null}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
          {showShareButton && (
            <button
              type="button"
              style={btnPrimary}
              onClick={() => {
                if (typeof onBeginCheck === 'function') onBeginCheck();
                if (typeof onStartScreenShare === 'function') {
                  Promise.resolve(onStartScreenShare()).catch(() => {});
                }
              }}
            >
              Start Screen Sharing
            </button>
          )}
          {showFullscreenButton && (
            <button
              type="button"
              style={btnPrimary}
              onClick={() => {
                if (typeof onBeginCheck === 'function') onBeginCheck();
                if (typeof onEnterFullscreen === 'function') onEnterFullscreen();
              }}
            >
              Go Fullscreen
            </button>
          )}
        </div>
      </div>
    );
  }

  const node = <div style={shell}>{inner}</div>;
  if (typeof document === 'undefined') return node;
  return ReactDOM.createPortal(node, document.body);
}
